import { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import "./TreeViewInternationalFilter.scss";
import { LeafBackdrop } from "../../../common/leaf/LeafBackdrop/LeafBackdrop";
import { LeafToggleSwitch } from "../../../common/leaf/LeafToggleSwitch/LeafToggleSwitch";
import LeafFilterButton from "../../../common/leaf/LeafFilter/LeafFilterButton/LeafFilterButton";
import { FocusTrap } from "../../../common/components/FocusTrap/FocusTrap";
import { IconFilter } from "../../../common/icons/IconFilter/IconFilter";
import { IconClose } from "../../../common/icons/IconClose/IconClose";
import { IconCheck } from "../../../common/icons/IconCheck/IconCheck";
import { store as accBootStore } from "../../Lit/acc-boot/acc-boot.js";
import defaultContent from "../../../content/los";
import { useIsMobile } from "../../ActionReports/hooks/useIsMobile";
import { useFullscreenPortalTarget } from "../../../common/util/fullscreenPortalTarget";
import {
  hasGranularInternationalFilterFlags,
  resolveInternationalFilterFlags,
} from "../../RwLos/LosToolbar/LosFilterModal/LosFilterModal";
import type { LosUiHeaderFilterFlags } from "../../RwLos/LosToolbar/LosFilterModal/LosFilterModal";

interface TreeViewInternationalFilterProps {
  inInternational: boolean;
  hideInternationalDownlines: boolean;
  hideVolume: boolean;
  hideZeroVolumeAbos: boolean;
  onApplyVolumeFilters: (hideVolume: boolean, hideZeroVolumeAbos: boolean) => void;
  /** Market uiHeader filter flags. International rows are only gated when a granular
   *  international flag is set; otherwise all rows render (existing behaviour). */
  uiHeader?: LosUiHeaderFilterFlags;
}

/**
 * Restyles the two mutually-exclusive international filters that the legacy Lit
 * acc-wc-los-header "more menu" already exposes for the non-fullscreen LOS grid
 * (International/In-Market Frontlines Only, and International Downlines) as a
 * compact anchored panel for TreeView's fullscreen toolbar. Selections are
 * buffered locally and only committed to the shared acc-boot store — the same
 * {inInternational, hideInternationalDownlines} fields the Lit menu writes to —
 * when the user clicks Apply. Mirrors LosFilterModal's row shape: every row is
 * a static "Show ___" toggle, on = shown.
 *
 * Also exposes the legacy grid's "Show Volume" and "Show ABOs With Zero Volume"
 * toggles, ported here since TreeView has no acc-boot equivalent for them —
 * their committed state is owned by TreeView.tsx and applied via onApplyVolumeFilters.
 */
export const TreeViewInternationalFilter = ({
  inInternational,
  hideInternationalDownlines,
  hideVolume,
  hideZeroVolumeAbos,
  onApplyVolumeFilters,
  uiHeader,
}: TreeViewInternationalFilterProps) => {
  const { t } = useTranslation(["los"]);
  const isMobile = useIsMobile();
  // Below tablet width the panel switches to position: fixed (see the .scss's
  // less_than_tablet block) so it stays anchored to the viewport instead of the
  // trigger button. But TreeViewFullscreenControls' backdrop-filter creates a
  // new containing block for fixed descendants (same rule as `transform`), so
  // a fixed-position panel left in place there ends up anchored to that small,
  // content-sized ancestor instead of the real viewport. Portaling past it
  // resolves position: fixed against the true viewport again.
  const portalTarget = useFullscreenPortalTarget();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [mobilePanelStyle, setMobilePanelStyle] = useState<React.CSSProperties>({});
  const [draftShowInternationalFrontlines, setDraftShowInternationalFrontlines] = useState(inInternational);
  const [draftShowInMarketFrontlines, setDraftShowInMarketFrontlines] = useState(!inInternational);
  const [draftShowInternationalDownlines, setDraftShowInternationalDownlines] = useState(!hideInternationalDownlines);
  const [draftShowVolume, setDraftShowVolume] = useState(!hideVolume);
  const [draftShowZeroVolumeAbos, setDraftShowZeroVolumeAbos] = useState(!hideZeroVolumeAbos);

  const PANEL_WIDTH_PX = 359;
  const VIEWPORT_MARGIN_PX = 16;

  const computeMobilePanelStyle = useCallback((): React.CSSProperties => {
    if (!triggerRef.current) return {};
    const rect = triggerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const width = Math.min(PANEL_WIDTH_PX, viewportWidth - VIEWPORT_MARGIN_PX * 2);
    const fitsAtTriggerLeft = rect.left + width + VIEWPORT_MARGIN_PX <= viewportWidth;

    return fitsAtTriggerLeft
      ? { top: rect.bottom + 8, left: rect.left, right: "auto", width }
      : { top: rect.bottom + 8, left: "50%", right: "auto", width, transform: "translateX(-50%)" };
  }, []);

  const openPanel = useCallback(() => {
    setDraftShowInternationalFrontlines(inInternational);
    setDraftShowInMarketFrontlines(!inInternational);
    setDraftShowInternationalDownlines(!hideInternationalDownlines);
    setDraftShowVolume(!hideVolume);
    setDraftShowZeroVolumeAbos(!hideZeroVolumeAbos);

    if (isMobile) setMobilePanelStyle(computeMobilePanelStyle());

    setIsOpen(true);
  }, [inInternational, hideInternationalDownlines, hideVolume, hideZeroVolumeAbos, isMobile, computeMobilePanelStyle]);

  // If the viewport crosses the mobile breakpoint (or is resized/rotated
  // within it) while the panel is already open, mobilePanelStyle would
  // otherwise stay stale from whenever openPanel last ran (or empty, if it
  // was opened above the breakpoint) — recompute live so it never renders
  // position: fixed with no offsets.
  useEffect(() => {
    if (!isOpen || !isMobile) return;

    setMobilePanelStyle(computeMobilePanelStyle());

    const handleResize = () => setMobilePanelStyle(computeMobilePanelStyle());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isOpen, isMobile, computeMobilePanelStyle]);

  const closePanel = useCallback(() => setIsOpen(false), []);

  // Opt-in gating: only markets that configure granular international flags get rows hidden.
  const intlVisibility = hasGranularInternationalFilterFlags(uiHeader)
    ? resolveInternationalFilterFlags(uiHeader)
    : {
        showIntlFrontlines: true,
        showInMarketFrontlines: true,
        showIntlDownlines: true,
        lockInMarketFrontlines: false,
      };
  const { lockInMarketFrontlines } = intlVisibility;

  // Counts only visible rows; with no granular flags all rows are visible (existing behaviour)
  const activeCount = [
    intlVisibility.showIntlFrontlines && inInternational,
    intlVisibility.showInMarketFrontlines && !inInternational,
    intlVisibility.showIntlDownlines && !hideInternationalDownlines,
    !hideVolume,
    !hideZeroVolumeAbos,
  ].filter(Boolean).length;

  // Show International Frontlines Only — mutually exclusive with Show In-Market
  // Frontlines Only. Turning ON also enables Show International Downlines,
  // since downlines only make sense while viewing International Frontlines Only.
  // Turning OFF resets Downlines OFF too.
  const handleInternationalFrontlinesToggle = useCallback(() => {
    setDraftShowInternationalFrontlines((prev) => {
      const next = !prev;
      setDraftShowInMarketFrontlines(!next);
      setDraftShowInternationalDownlines(next);
      return next;
    });
  }, []);

  // Show In-Market Frontlines Only — mutually exclusive with Show International
  // Frontlines Only. Turning ON hides international downlines, since they can't
  // coexist with an in-market-only view.
  const handleInMarketFrontlinesToggle = useCallback(() => {
    // Locked ON — never switch to international mode when those rows are hidden
    if (lockInMarketFrontlines) return;
    setDraftShowInMarketFrontlines((prev) => {
      const next = !prev;
      setDraftShowInternationalFrontlines(!next);
      if (next) {
        setDraftShowInternationalDownlines(false);
      }
      return next;
    });
  }, [lockInMarketFrontlines]);

  // Show International Downlines — turning ON can't coexist with Show In-Market
  // Frontlines Only, so it forces International Frontlines Only on instead.
  // Turning OFF has no such conflict and leaves the frontlines toggles as-is.
  const handleDownlinesToggle = useCallback(() => {
    setDraftShowInternationalDownlines((prev) => {
      const next = !prev;
      if (next) {
        setDraftShowInternationalFrontlines(true);
        setDraftShowInMarketFrontlines(false);
      }
      return next;
    });
  }, []);

  const handleVolumeToggle = useCallback(() => {
    setDraftShowVolume((prev) => !prev);
  }, []);

  const handleZeroVolumeToggle = useCallback(() => {
    setDraftShowZeroVolumeAbos((prev) => !prev);
  }, []);

  const handleApply = useCallback(() => {
    accBootStore.setState({
      inInternational: draftShowInternationalFrontlines,
      hideInternationalDownlines: !draftShowInternationalDownlines,
    });
    onApplyVolumeFilters(!draftShowVolume, !draftShowZeroVolumeAbos);
    closePanel();
  }, [
    draftShowInternationalFrontlines,
    draftShowInternationalDownlines,
    draftShowVolume,
    draftShowZeroVolumeAbos,
    onApplyVolumeFilters,
    closePanel,
  ]);

  const labelShowInternationalFrontlines = t(
    "filterModal.showInternationalFrontlinesOnly",
    defaultContent.filterModal.showInternationalFrontlinesOnly,
  );
  const labelShowInMarketFrontlines = t(
    "filterModal.showInMarketFrontlinesOnly",
    defaultContent.filterModal.showInMarketFrontlinesOnly,
  );
  const labelShowInternationalDownlines = t(
    "filterModal.showInternationalDownlines",
    defaultContent.filterModal.showInternationalDownlines,
  );
  const labelShowVolume = t("filterModal.showData", defaultContent.filterModal.showData);
  const labelShowZeroVolumeAbos = t(
    "filterModal.showABOsWithZeroVolume",
    defaultContent.filterModal.showABOsWithZeroVolume,
  );

  const panelContent = isOpen && (
    <>
      <LeafBackdrop variant="invisible" handleBackdropClick={closePanel} />
      <FocusTrap
        handleOnClose={closePanel}
        className="tree-view-international-filter__panel"
        role="dialog"
        aria-label={String(t("filterModal.title", defaultContent.filterModal.title))}
        style={isMobile ? mobilePanelStyle : undefined}
      >
        <div className="tree-view-international-filter__header">
          <span className="tree-view-international-filter__title">
            {t("filterModal.title", defaultContent.filterModal.title)}
          </span>
          <button
            type="button"
            className="tree-view-international-filter__close"
            onClick={closePanel}
            aria-label={String(t("filterModal.close", defaultContent.filterModal.close))}
          >
            <IconClose width="0.875rem" height="0.875rem" color="black" />
          </button>
        </div>
        <div className="tree-view-international-filter__body">
          {intlVisibility.showIntlFrontlines && (
            <LeafToggleSwitch
              isToggled={draftShowInternationalFrontlines}
              onToggle={handleInternationalFrontlinesToggle}
              name="tree-view-international-filter-international-frontlines"
              label={String(labelShowInternationalFrontlines)}
              toggleOnIcon={<IconCheck color="blue" />}
            />
          )}
          {intlVisibility.showInMarketFrontlines && (
            <LeafToggleSwitch
              isToggled={lockInMarketFrontlines || draftShowInMarketFrontlines}
              onToggle={handleInMarketFrontlinesToggle}
              name="tree-view-international-filter-in-market-frontlines"
              label={String(labelShowInMarketFrontlines)}
              toggleOnIcon={<IconCheck color="blue" />}
              disabled={lockInMarketFrontlines}
            />
          )}
          {intlVisibility.showIntlDownlines && (
            <LeafToggleSwitch
              isToggled={draftShowInternationalDownlines}
              onToggle={handleDownlinesToggle}
              name="tree-view-international-filter-downlines"
              label={String(labelShowInternationalDownlines)}
              toggleOnIcon={<IconCheck color="blue" />}
            />
          )}
          <LeafToggleSwitch
            isToggled={draftShowVolume}
            onToggle={handleVolumeToggle}
            name="tree-view-international-filter-volume"
            label={String(labelShowVolume)}
            toggleOnIcon={<IconCheck color="blue" />}
          />
          <LeafToggleSwitch
            isToggled={draftShowZeroVolumeAbos}
            onToggle={handleZeroVolumeToggle}
            name="tree-view-international-filter-zero-volume"
            label={String(labelShowZeroVolumeAbos)}
            toggleOnIcon={<IconCheck color="blue" />}
          />
        </div>
        <div className="tree-view-international-filter__footer">
          <LeafFilterButton
            text={String(t("filterModal.cancel", defaultContent.filterModal.cancel))}
            onButtonClick={closePanel}
          />
          <LeafFilterButton
            text={String(t("filterModal.apply", defaultContent.filterModal.apply))}
            onButtonClick={handleApply}
            theme="dark"
          />
        </div>
      </FocusTrap>
    </>
  );

  return (
    <div className="tree-view-international-filter">
      <button
        ref={triggerRef}
        className={`tree-view-international-filter__trigger${isOpen ? " tree-view-international-filter__trigger--active" : ""} tree-view-poc__control-button`}
        onClick={openPanel}
        aria-label={String(
          activeCount > 0
            ? t("toolbar.filtersActiveAriaLabel", defaultContent.toolbar.filtersActiveAriaLabel, {
                count: activeCount,
              })
            : t("toolbar.filtersAriaLabel", defaultContent.toolbar.filtersAriaLabel),
        )}
        data-testid="tree-view-international-filter-trigger"
      >
        <IconFilter />
        {activeCount > 0 && (
          <span
            className="tree-view-international-filter__badge"
            aria-hidden="true"
            data-testid="tree-view-international-filter-badge"
          >
            {activeCount > 99 ? "99+" : activeCount}
          </span>
        )}
      </button>
      {/* Below tablet width the panel is position: fixed and needs to be portaled
          past TreeViewFullscreenControls' backdrop-filter ancestor to resolve
          against the real viewport (see the comment on portalTarget above).
          Above that width it stays an inline sibling, anchored to the trigger
          via position: absolute, so no portal is needed or wanted. */}
      {isMobile ? panelContent && ReactDOM.createPortal(panelContent, portalTarget) : panelContent}
    </div>
  );
};
